import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import StatTile from './components/StatTile';
import FilterBar from './components/FilterBar';
import FactoryCard from './components/FactoryCard';
import { fetchFactoryCards } from './data';

// Season/Grade filter OPTIONS in FilterBar are still a hardcoded list (no real season/grade reference
// table exists on mi_mills), but the FILTERING itself is now real — each card carries activeSeasons/
// activeGrades derived from real mill_price_quotes rows (data.js), currently sparse like everything
// else that depends on that table across this app.

// ── Skeleton Card ──────────────────────────────────────────────────────────
const SkeletonCard = () => (
  <div className="bg-white rounded-2xl p-5 animate-pulse" style={{ border: '1px solid #e8ddd0', borderTop: '3px solid #e8ddd0', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
    <div className="h-5 rounded-lg bg-gray-200 w-2/3 mb-2" />
    <div className="h-3 rounded bg-gray-100 w-1/2 mb-4" />
    <div className="flex gap-2 mb-4">
      {[1,2,3,4]?.map(i => <div key={i} className="h-6 w-16 rounded-full bg-gray-100" />)}
    </div>
    <div className="flex gap-2 mb-4">
      <div className="h-8 w-32 rounded-lg bg-gray-100" />
      <div className="h-8 w-40 rounded-lg bg-gray-100" />
    </div>
    <div className="rounded-xl h-28 bg-amber-50 mt-3" />
    <div className="rounded-xl h-12 bg-gray-100 mt-3" />
  </div>
);

// ── Empty State ────────────────────────────────────────────────────────────
const EmptyState = () => (
  <div className="col-span-full flex flex-col items-center justify-center py-20 text-center">
    <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ background: '#fef3c7' }}>
      <Icon name="SearchX" size={28} color="#d97706" />
    </div>
    <h3 className="text-lg font-bold mb-1" style={{ color: '#1f2937' }}>No factories match your filters</h3>
    <p className="text-sm" style={{ color: '#6b7280' }}>Try adjusting your search or filter criteria to find mills.</p>
  </div>
);

// ── Main Page ──────────────────────────────────────────────────────────────
const FactoryRateIntelligenceCenter = () => {
  const [loading, setLoading] = useState(true);
  const [factories, setFactories] = useState([]);
  const [fetchError, setFetchError] = useState(null);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    state: 'All States',
    district: 'All Districts',
    season: 'All Seasons',
    grade: 'All Grades',
    activeQuotaOnly: true,
  });
  const [visibleCount, setVisibleCount] = useState(12);
  // Deep-link target from My Submissions' "Update & Resubmit" (Expired tab) — see
  // /factory-rate-intelligence-center?millId=... . Same pattern as the Mandi ->
  // Marketplace highlight/scroll continuation built earlier this session.
  const [autoExpandMillId] = useState(() => new URLSearchParams(window.location?.search)?.get('millId'));
  const autoExpandAttemptedRef = React.useRef(false);

  // Real fetch — see data.js for the full locked-query translation + the LATERAL-join replacement.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setFetchError(null);
      const { cards, error } = await fetchFactoryCards();
      if (cancelled) return;
      if (error) {
        setFetchError(error);
        setFactories([]);
      } else {
        setFactories(cards);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  // Filtered factories
  const filtered = useMemo(() => {
    return factories?.filter(f => {
      // Real check this time — thisMonthQuota lives under miData, not top-level on the card (the
      // previous version checked the always-true activeQuotaInTrailing12m flag, which every fetched
      // card already satisfies by construction, making the toggle structurally inert).
      if (filters?.activeQuotaOnly && !(f?.miData?.thisMonthQuota > 0)) return false;
      // Search
      if (search?.trim() && !f?.name?.toLowerCase()?.includes(search?.toLowerCase())) return false;
      // State
      if (filters?.state !== 'All States' && f?.state !== filters?.state) return false;
      // District
      if (filters?.district !== 'All Districts' && f?.district !== filters?.district) return false;
      // Season
      if (filters?.season !== 'All Seasons' && !f?.activeSeasons?.includes(filters?.season)) return false;
      // Grade
      if (filters?.grade !== 'All Grades' && !f?.activeGrades?.includes(filters?.grade)) return false;
      return true;
    });
  }, [factories, search, filters]);

  // Render-level pagination only — the fetch above always pulls the full matching set (needed for
  // accurate filter dropdowns, the "N mills found" count, and the stat tiles below regardless of how
  // many cards are actually rendered). A new filter/search always restarts the visible page at 12,
  // rather than preserving a stale count from a larger/smaller previous result set.
  useEffect(() => {
    setVisibleCount(12);
  }, [search, filters]);

  const visibleFactories = filtered?.slice(0, visibleCount);
  const hasMore = visibleCount < (filtered?.length || 0);

  useEffect(() => {
    if (!autoExpandMillId || loading || autoExpandAttemptedRef.current) return;
    const targetIndex = filtered?.findIndex((f) => f?.id === autoExpandMillId);
    if (targetIndex === -1) return; // not found yet (filters may still be settling) or genuinely absent
    autoExpandAttemptedRef.current = true;
    if (targetIndex >= visibleCount) {
      setVisibleCount(targetIndex + 1);
    }
  }, [autoExpandMillId, loading, filtered, visibleCount]);

  // Analytics — reflect the active-quota mill universe, independent of search/state/district filters
  const activeMills = factories?.filter(f => f?.activeQuotaInTrailing12m);
  const totalActiveMillsCount = activeMills?.length;
  const statesCovered = [...new Set(activeMills.map(f => f.state))]?.length;
  const thisMonthAggregate = activeMills?.reduce((sum, f) => sum + (f?.miData?.thisMonthQuota || 0), 0);
  const updatedToday = activeMills?.filter(f => f?.updatedToday)?.length;

  const statTiles = [
    {
      icon: 'Factory',
      label: 'Total Active Mills',
      value: totalActiveMillsCount,
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    },
    {
      icon: 'Map',
      label: 'States Covered',
      value: statesCovered,
      gradient: 'linear-gradient(135deg, #ff8c00 0%, #ea580c 100%)',
    },
    {
      icon: 'BarChart3',
      label: "This Month's Aggregate Quota (MT)",
      value: thisMonthAggregate?.toLocaleString('en-IN'),
      gradient: 'linear-gradient(135deg, #f97316 0%, #f59e0b 100%)',
    },
    {
      icon: 'RefreshCw',
      label: 'Mills Updated Today',
      value: updatedToday,
      gradient: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
    },
  ];

  return (
    <>
      <Helmet>
        <title>Factory Rate Intelligence Center – SugarHathi</title>
        <meta name="description" content="Staff tool to update and monitor sugar mill rates with government market data." />
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
      </Helmet>
      <div className="min-h-screen" style={{ background: '#f8f1e9', fontFamily: "'DM Sans', sans-serif" }}>
        <RoleBasedHeader />
        {/* ── Top Bar ── */}
        <header
          className="sticky top-0 z-40 bg-white border-b"
          style={{ borderColor: '#e8ddd0', boxShadow: '0 2px 4px rgba(0,0,0,0.06)' }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              {/* Title */}
              <div className="flex items-center gap-3 flex-1">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: 'linear-gradient(135deg, #f59e0b, #ff8c00)' }}
                >
                  <Icon name="Zap" size={20} color="#ffffff" />
                </div>
                <div>
                  <h1 className="text-xl font-bold leading-tight" style={{ color: '#1f2937' }}>
                    Factory Rate Intelligence Center
                  </h1>
                  <p className="text-xs font-medium" style={{ color: '#6b7280' }}>
                    Staff tool · Sugar mill rate management
                  </p>
                </div>
              </div>

              {/* Search box */}
              <div className="relative w-full sm:w-72">
                <Icon name="Search" size={15} color="#9ca3af" className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search by mill name…"
                  value={search}
                  onChange={e => setSearch(e?.target?.value)}
                  className="w-full pl-9 pr-4 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                  style={{ borderColor: '#e8ddd0', background: '#fafaf9', color: '#1f2937' }}
                />
                {search && (
                  <button
                    onClick={() => setSearch('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                  >
                    <Icon name="X" size={13} color="#9ca3af" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
          {/* ── Filter Bar ── */}
          <FilterBar filters={filters} onChange={setFilters} factories={factories} />

          {/* ── Analytics Strip ── */}
          <section>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {statTiles?.map(tile => (
                <StatTile
                  key={tile?.label}
                  icon={tile?.icon}
                  label={tile?.label}
                  value={tile?.value}
                  gradient={tile?.gradient}
                />
              ))}
            </div>
          </section>

          {/* ── Factory Cards ── */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm font-semibold" style={{ color: '#6b7280' }}>
                {loading ? 'Loading…' : `${filtered?.length} mill${filtered?.length !== 1 ? 's' : ''} found`}
              </p>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                {[1, 2, 3, 4, 5, 6]?.map(i => <SkeletonCard key={i} />)}
              </div>
            ) : fetchError ? (
              <div className="col-span-full flex flex-col items-center justify-center py-20 text-center">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ background: '#fee2e2' }}>
                  <Icon name="AlertTriangle" size={28} color="#DC143C" />
                </div>
                <h3 className="text-lg font-bold mb-1" style={{ color: '#1f2937' }}>Couldn't load mill data</h3>
                <p className="text-sm" style={{ color: '#6b7280' }}>{fetchError}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                {filtered?.length === 0 ? (
                  <EmptyState />
                ) : (
                  visibleFactories?.map(factory => (
                    <FactoryCard key={factory?.id} factory={factory} autoExpand={factory?.id === autoExpandMillId} />
                  ))
                )}
              </div>
            )}

            {!loading && !fetchError && hasMore && (
              <div className="flex justify-center mt-6">
                <button
                  onClick={() => setVisibleCount(c => Math.min(c + 12, filtered?.length))}
                  className="w-full sm:w-auto px-6 py-3 rounded-xl font-bold text-sm transition hover:opacity-90 active:scale-95"
                  style={{ background: '#fff', border: '1px solid #f59e0b', color: '#d97706' }}
                >
                  Showing {visibleFactories?.length} of {filtered?.length} mills — Load More
                </button>
              </div>
            )}
          </section>

          {/* Footer caption */}
          <div className="flex items-center justify-center gap-2 py-4 text-xs" style={{ color: '#9ca3af' }}>
            <Icon name="ShieldCheck" size={13} color="#d97706" />
            Staff-only tool · SugarHathi Market Intelligence · Mill &amp; quota data is real; Season/Grade filters and rate-entry history are demo-only for now
          </div>
        </main>
      </div>
    </>
  );
};

export default FactoryRateIntelligenceCenter;
